import * as React from 'react';
import { compose, setDisplayName } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { connect } from 'react-redux';
import { CellIndex, Camera, UtcTime, Stream } from 'types';
import { State } from 'reducers';
import { getCameraFromCell, getStreamPosition, isLive } from 'selectors/monitoringSelectors';
import { videoConnectRequest, videoConnectFailure, videoConnectSuccess, videoDisconnected } from 'actions/monitoringActions';
import { WebRtcStreamer } from 'api/webRtcStreamer';
import { deepEqual } from 'util/deepEqual';
import { translateError } from 'sagas/app/handleError';

import './VideoView.css';
import { logout } from 'api';


export class RawVideoView extends React.Component<Props> {
    webRtcStreamer: WebRtcStreamer;
    videoElement: HTMLVideoElement | null;

    constructor(props: Props) {
        super(props);
        this.videoElement = null;
        this.webRtcStreamer = new WebRtcStreamer();
    }

    private async connectVideo(streamPosition: UtcTime) {
        const { stream, visible, t } = this.props;
        if (!stream || !visible) {
            return;
        }
        const { live, onVideoRequest, onVideoSuccess, onVideoFailure } = this.props;
        const { deviceId, streamIndex } = stream;
        let utcActualTime: UtcTime = 0;
        try {
            onVideoRequest();
            utcActualTime = await this.webRtcStreamer.connect(this.videoElement, deviceId, streamIndex, live ? 0 : streamPosition);
        }
        catch (err) {
            const { localError, invalidSession } = translateError(err, t);
            onVideoFailure(localError, invalidSession);
            return;
        }

        onVideoSuccess(utcActualTime, live);
    }

    async componentDidMount() {
        await this.connectVideo(this.props.streamPosition);
    }

    componentWillUnmount() {
        const { visible, onDisconnect } = this.props;
        if (visible) {
            const streamPosition = this.webRtcStreamer.disconnect();
            onDisconnect(streamPosition);
        }
    }

    async componentDidUpdate(prevProps: OwnProps & StateProps & DispProps) {
        // If the control is being hidden, disconnect video and return the
        // time stamp so we can update Redux store with the latest time.
        // (i.e. so we can seek to the correct frame)
        const { visible, onDisconnect } = this.props;
        if (prevProps.visible && !visible) {
            const streamPosition = this.webRtcStreamer.disconnect();
            onDisconnect(streamPosition);
            return;
        }

        const { live: prevLive, stream: prevStream } = prevProps;
        const { live, stream } = this.props;

        // If the stream changed then initialize the new one
        // Note, this will also automatically dispose of the previous connection.
        if (!deepEqual(stream, prevStream) || live !== prevLive) {
            let { streamPosition } = this.props;
            try {
                if (prevStream) {
                    streamPosition = this.webRtcStreamer.disconnect();
                }
            }
            catch (err) {
                // Log but do nothing about it
                console.error(err);
            }

            this.webRtcStreamer = new WebRtcStreamer();
            await this.connectVideo(live ? 0 : streamPosition);
        }
    }

    render() {
        const { cellIndex } = this.props;
        return (
            <video
                id={ `video-${cellIndex}` }
                ref={ _ => this.videoElement = _ }
                autoPlay={ true }
                muted={ true }
            />
        );
    }
}

type OwnProps = {
    cellIndex: CellIndex;
    stream: Stream;
    visible: boolean;
};
type StateProps = {
    streamPosition: UtcTime;
    camera: Camera | null;
    live: boolean;
};
type DispProps = {
    onVideoRequest: () => void;
    onVideoSuccess: (utcTime: UtcTime, live: boolean) => void;
    onVideoFailure: (err: Error, invalidSession: boolean) => void;
    onDisconnect: (streamPosition: UtcTime) => void;
};

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => ({
    streamPosition: getStreamPosition(state, ownProps),
    camera: getCameraFromCell(state, ownProps),
    live: isLive(state, ownProps),
});
const mapDispatchToProps = (dispatch: Function, { cellIndex }: OwnProps): DispProps => ({
    onVideoRequest: () => dispatch(videoConnectRequest(cellIndex)),
    onVideoSuccess: (utcTime: UtcTime, live: boolean) => dispatch(videoConnectSuccess({ cellIndex, utcTime, live })),
    onVideoFailure: (err, invalidSession) => {
        dispatch(videoConnectFailure(err, { cellIndex }));
        if (invalidSession) {
            dispatch(logout());
        }
    },
    onDisconnect: (utcTime: UtcTime) => dispatch(videoDisconnected({ cellIndex, utcTime })),
});

type Props = OwnProps & StateProps & DispProps & TranslateProps;

export const VideoView = compose<Props, OwnProps>(
    setDisplayName('VideoView'),
    translate(),
    connect<StateProps, DispProps, OwnProps>(mapStateToProps, mapDispatchToProps),
)(RawVideoView);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/VideoView.tsx